import { DeleteOutlined } from '@ant-design/icons';
import { Table } from 'antd';
import { useFormikContext } from 'formik';
import React, { type Dispatch, type SetStateAction, useCallback, useRef } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { getMenuItemValue } from '.';
import { type MenuItemSpecsFragment } from '../../../../api/fragments/MenuItemSpecs';
import useTranslatedString from '../../../../utilities/useTranslatedString';
import ResponsiveStyledTable from '../../../shared/ResponsiveStyledTable';

interface DraggableBodyRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
    index: number;
    moveRow: (dragIndex: number, hoverIndex: number) => void;
    title: string;
    dataIndex: string | string[];
}

const type = 'DraggableBodyRow';

const DraggableBodyRow = ({
    index,
    moveRow,
    title,
    dataIndex,
    className,
    style,
    ...restProps
}: DraggableBodyRowProps) => {
    const ref = useRef<HTMLTableRowElement>(null);
    const [{ isOver, dropClassName }, drop] = useDrop({
        accept: type,
        collect: monitor => {
            const { index: dragIndex } = monitor.getItem() || {};
            if (dragIndex === index) {
                return {};
            }

            return {
                isOver: monitor.isOver(),
                dropClassName: dragIndex < index ? ' drop-over-downward' : ' drop-over-upward',
            };
        },
        drop: (item: { index: number }) => {
            moveRow(item.index, index);
        },
    });
    const [, drag] = useDrag({
        type,
        item: { index },
        collect: monitor => ({
            isDragging: monitor.isDragging(),
        }),
    });
    drop(drag(ref));

    return (
        <tr
            ref={ref}
            className={`${className}${isOver ? dropClassName : ''}`}
            style={{ cursor: 'move', ...style }}
            {...restProps}
        />
    );
};

type RouterMenuItemTableProps = {
    dataSource: MenuItemSpecsFragment[];
    setDataSource: Dispatch<SetStateAction<MenuItemSpecsFragment[]>>;
};

export const getMenuItemPathName = item => {
    switch (item.__typename) {
        case 'MenuCustomPathItem':
            return item.pathname;

        case 'MenuEndpointItem':
            return item.endpoint.pathname;

        default:
            return '';
    }
};

const RouterMenuItemTable = ({ dataSource, setDataSource }: RouterMenuItemTableProps) => {
    const { t } = useTranslation('routerDetails');
    const { setValues } = useFormikContext();
    const translate = useTranslatedString();

    const components = {
        body: {
            row: DraggableBodyRow,
        },
    };

    const onSortRow = useCallback(
        (record, index) =>
            ({
                index,
                moveRow,
            }) as React.HTMLAttributes<any>,
        []
    );

    const removeMenuItem = useCallback(
        record => {
            const newDataSource = dataSource.filter(data => data.id !== record.id);
            setDataSource(newDataSource);
            setValues(getMenuItemValue(newDataSource));
        },
        [dataSource, setDataSource, setValues]
    );

    const moveRow = useCallback(
        (dragIndex: number, hoverIndex: number) => {
            setDataSource(previousState => {
                const dragRow = previousState[dragIndex];
                const remaining = previousState.filter(i => i !== dragRow);

                // return newly sorted row
                const newDataSource = [...remaining.slice(0, hoverIndex), dragRow, ...remaining.slice(hoverIndex)];
                setValues(getMenuItemValue(newDataSource));

                return newDataSource;
            });
        },
        [setDataSource, setValues]
    );

    return (
        <DndProvider backend={HTML5Backend}>
            <ResponsiveStyledTable
                components={components}
                dataSource={dataSource}
                onRow={onSortRow}
                pagination={false}
                rowKey="index"
            >
                <Table.Column
                    dataIndex="label"
                    render={(value, record, index) => translate(value)}
                    title={t('routerDetails:menuItems.columns.label')}
                />
                <Table.Column
                    dataIndex="__typename"
                    render={(value, record, index) => t(`routerDetails:menuItems.menuItemTypename.${value}`)}
                    title={t('routerDetails:menuItems.columns.type')}
                />
                <Table.Column dataIndex="icon" title={t('routerDetails:menuItems.columns.icon')} />
                <Table.Column
                    dataIndex="pathname"
                    render={(value, record, index) => getMenuItemPathName(record)}
                    title={t('routerDetails:menuItems.columns.pathname')}
                />
                <Table.Column
                    render={(value, record, index) => <DeleteOutlined onClick={() => removeMenuItem(record)} />}
                />
            </ResponsiveStyledTable>
        </DndProvider>
    );
};

export default RouterMenuItemTable;
